// CONTRACT:C3-CAPABILITY-PLUGIN.1.0
/** THE LIVE ADS-B CAPABILITY — real aircraft over the Hormuz AO, published alongside the
 *  sealed/ambient tracks. An SDK-pure capability (mirrors multiIntCapability): it touches
 *  NOTHING but HostContext + the contract types + its OWN data source (adsbSource.ts) — so the
 *  dogfood-check SDK-purity scanner and the no-bypass-check engine-write scanner both stay green.
 *
 *  WHAT IT DOES (the density/multi-INT shape exactly):
 *   - On activate it POLLS OpenSky Network (anonymous REST, NO API KEY) every ≥20s and maps
 *     each aircraft state vector → a clear-shell `track` air envelope, then PUBLISHES it via
 *     ctx.data.publish. It does NOT paint the map and does NOT write the engine — the HOST runs
 *     each envelope through the PDP and paints the cleared-visible slice (here: all of it, since
 *     public ADS-B is UNCLASSIFIED). The capability only contributes a layer + publishes.
 *   - PLANE-TYPE BUCKETING (the headline): each aircraft is bucketed {light, small, large,
 *     heavy, rotorcraft, military/special, unknown} from OpenSky's `category` field (extended
 *     form), with a callsign/origin heuristic fallback when category is 0/absent. The bucket
 *     rides in `properties.plane_bucket` so it is filterable.
 *   - OFFLINE-GRACEFUL: if OpenSky is unreachable / throttled / returns no states, it falls
 *     back to a BUNDLED snapshot fixture (adsbSnapshot.json — a real OpenSky capture) so the
 *     capability still publishes in dev/offline and tests are deterministic. Network errors are
 *     caught + logged, NEVER thrown into the host. A `mode` opt selects live-vs-snapshot.
 *
 *  CLEAR-SHELL ONLY: public ADS-B is UNCLASSIFIED — payload_mode:"clear", leakage_profile:
 *  "clear", NO sealed/encrypted inner, NO fake decrypt. Civil traffic is affiliation "unknown"
 *  (NEVER hostile by default). */

import type {
  Capability,
  CapabilityManifest,
  Disposable,
  HostContext,
  LayerSpec,
} from "../../../../packages/contracts-ts/src/index.ts";
import {
  ADSB_KIND,
  DEFAULT_POLL_MS,
  MIN_POLL_MS,
  fetchLiveStates,
  responseToEnvelopes,
  snapshotEnvelopes,
  deadReckonAdsbEnvelope,
  ADSB_SNAPSHOT_RESPONSE,
  type OpenSkyResponse,
} from "./adsbSource.ts";

/** Dead-reckon step cadence (ms). Aircraft advance forward from the last fix every ~1 s so the
 *  sky glides between the ≥20 s OpenSky polls (the COP interpolates further between steps). */
const ADSB_STEP_MS = 1_000;

/** The capability id. */
export const ADSB_CAP_ID = "scop:cap:adsb";

/** The deck layer this capability owns — a DISTINCT layer id so it never fights another
 *  capability over the registry, though it draws the shared `track` kind (the host's per-kind
 *  painter routes cleared-visible track envelopes to it). */
const LAYER_ID = "scop:layer:adsb";

/** THE MANIFEST — declared BEFORE activation. ADS-B contributes ONLY a layer (it has no
 *  commands/panels) and requires the (legacy-permissive, UNCLASSIFIED) subscribe permission.
 *  contributes is EXACTLY [layer] so a mutation that widens it goes red in the check. */
export const ADSB_MANIFEST: CapabilityManifest = {
  contributes: ["layer"],
  permissions: [
    {
      subject: {},
      action: "subscribe",
      resource: {
        classification: "UNCLASSIFIED",
        releasability: [],
        mission: [],
        caveats: [],
        originator_control: false,
        policy_ref: "policy://defense.local",
        payload_mode: "clear",
        leakage_profile: "clear",
      },
    },
  ],
};

/** The ADS-B layer spec — a scatter of air tracks, styled per the plane-type bucket so the
 *  buckets are visually distinguishable + filterable. */
const ADSB_LAYER: LayerSpec = {
  id: LAYER_ID,
  kinds: [ADSB_KIND],
  type: "ScatterplotLayer",
  props: { radiusMeters: 4_000 },
  updateTriggers: { getFillColor: "plane_bucket" },
};

/** Live-vs-snapshot switch. "live" polls OpenSky (falling back to the snapshot on failure);
 *  "snapshot" publishes the bundled fixture ONCE and never touches the network (dev/offline/
 *  deterministic-test). */
export type AdsbMode = "live" | "snapshot";

/** Construction options. */
export interface AdsbCapabilityOptions {
  /** Live polling vs the offline snapshot. Default: env COP_ADSB_MODE, else "live". */
  mode?: AdsbMode;
  /** Poll interval (ms) in live mode; floored at MIN_POLL_MS to respect anon rate limits. */
  pollMs?: number;
  /** Injected fetch (tests). Defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Injected logger (tests / host log routing). Defaults to console. */
  logger?: Pick<Console, "warn" | "info">;
  /** Stable capability id (default ADSB_CAP_ID). */
  id?: string;
}

/** Resolve the mode: explicit opt > env (COP_ADSB_MODE) > "live". */
function resolveMode(opt?: AdsbMode): AdsbMode {
  if (opt) return opt;
  const env =
    typeof process !== "undefined" ? process.env?.COP_ADSB_MODE : undefined;
  return env === "snapshot" ? "snapshot" : "live";
}

/** Build the LIVE ADS-B capability. NO host argument, NO PDP — the host's seam does the
 *  clearance filtering (a no-op here, since public ADS-B is UNCLASSIFIED). PUBLISH-ONLY. */
export function createAdsbCapability(opts: AdsbCapabilityOptions = {}): Capability {
  const id = opts.id ?? ADSB_CAP_ID;
  const mode = resolveMode(opts.mode);
  const pollMs = Math.max(opts.pollMs ?? DEFAULT_POLL_MS, MIN_POLL_MS);
  const fetchImpl = opts.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : undefined);
  const log = opts.logger ?? console;

  let disposables: Disposable[] = [];
  let timer: ReturnType<typeof setInterval> | undefined;
  let abort: AbortController | undefined;
  let disposed = false;
  // DEAD-RECKON state: the last published FIX (the freshest poll/snapshot envelopes) + its
  // wall-clock anchor. The ~1 s stepper advances these forward; each real poll/snapshot publish
  // RE-SEEDS them (so live data always wins and the forward extrapolation never compounds).
  let seeds: ReturnType<typeof responseToEnvelopes> = [];
  let fixMs = 0;

  /** Publish a whole OpenSky response as track envelopes through the bus, and RE-SEED the
   *  dead-reckon from this fresh fix. The HOST runs each through the PDP and paints the
   *  cleared-visible slice; we never paint or write the engine. */
  async function publishResponse(ctx: HostContext, resp: OpenSkyResponse): Promise<void> {
    const envelopes = responseToEnvelopes(resp);
    seeds = envelopes; // re-seed: a fresh poll/snapshot fix resets the forward extrapolation
    fixMs = Date.now();
    for (const env of envelopes) {
      if (disposed) return;
      await ctx.data.publish(env);
    }
  }

  /** One dead-reckon STEP: advance the cached fix forward (elapsed since the last fix) and
   *  re-publish so the sky GLIDES between the ≥20 s polls / animates the offline snapshot. Runs
   *  in BOTH modes; a no-op until the first fix is seeded. Stable ids ⇒ the dots MOVE (engine
   *  update), the host coalesces all per-step upserts into one frame write. */
  function stepOnce(ctx: HostContext): void {
    if (disposed || seeds.length === 0) return;
    const elapsedMs = Date.now() - fixMs;
    for (const seed of seeds) {
      void ctx.data.publish(deadReckonAdsbEnvelope(seed, elapsedMs));
    }
  }

  /** One LIVE poll: fetch OpenSky; on ANY failure (network/throttle/malformed) fall back to
   *  the bundled snapshot so the picture never goes blank. Errors are caught in
   *  fetchLiveStates (returns null) — this function NEVER throws into the host. */
  async function pollOnce(ctx: HostContext): Promise<void> {
    if (disposed || !fetchImpl) {
      // No fetch available (exotic runtime) ⇒ fall back to the snapshot.
      await publishResponse(ctx, ADSB_SNAPSHOT_RESPONSE);
      return;
    }
    abort = new AbortController();
    const live = await fetchLiveStates(fetchImpl, abort.signal);
    if (disposed) return;
    if (live && (live.states?.length ?? 0) > 0) {
      log.info?.(`[adsb] live OpenSky poll: ${live.states?.length ?? 0} aircraft over the AO`);
      await publishResponse(ctx, live);
    } else {
      // Unreachable / throttled / empty ⇒ offline-graceful fallback to the bundled snapshot.
      log.warn?.("[adsb] OpenSky unreachable/empty — falling back to the bundled snapshot");
      await publishResponse(ctx, ADSB_SNAPSHOT_RESPONSE);
    }
  }

  return {
    id,

    async activate(ctx: HostContext): Promise<void> {
      disposed = false;

      // 1. CONTRIBUTE the ADS-B layer (host-mediated registerLayer). The host creates the GPU
      //    layer and paints the cleared-visible track envelopes itself — we never touch the
      //    engine's write surface (no-bypass-check stays green).
      disposables = [ctx.registerLayer(ADSB_LAYER)];

      // 1b. DEAD-RECKON STEPPER (both modes): advance the cached fix forward ~1 Hz so the sky
      //     glides between polls AND the offline snapshot is no longer frozen. A no-op until the
      //     first fix is published below; cleared on deactivate via the disposable.
      const stepTimer = setInterval(() => stepOnce(ctx), ADSB_STEP_MS);
      disposables.push({ dispose: () => clearInterval(stepTimer) });

      // 2. SEED + POLL. Either mode publishes immediately so the map is alive on first paint.
      if (mode === "snapshot") {
        // Offline/deterministic: publish the bundled fixture ONCE, no network, no timer.
        log.info?.("[adsb] snapshot mode — publishing the bundled OpenSky fixture (offline)");
        await publishResponse(ctx, ADSB_SNAPSHOT_RESPONSE);
        return;
      }

      // LIVE: one poll now, then on a conservative interval (≥20s) to respect anon limits.
      await pollOnce(ctx);
      if (disposed) return;
      timer = setInterval(() => {
        // setInterval's callback must not reject; pollOnce already swallows all errors, but
        // guard with a catch so a stray rejection never reaches the host's event loop.
        void pollOnce(ctx).catch((err) => log.warn?.(`[adsb] poll error (ignored): ${String(err)}`));
      }, pollMs);
    },

    deactivate(): void {
      disposed = true;
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
      try {
        abort?.abort();
      } catch {
        // best-effort — aborting an in-flight fetch on teardown
      }
      for (const d of disposables.splice(0)) {
        try {
          d.dispose();
        } catch {
          // teardown is best-effort
        }
      }
    },
  };
}
